import React, { useCallback, useState, useEffect, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { MoonLoader } from 'react-spinners';
import { getAgentChats, getUserAgentChats } from '../../api/agents/agents';

import styles from './styles/AgentChatsList.module.css';

const AgentChatsList = ({
    agentSlug,
    chatType = 'agent-chats',
    accessToken = null,
    scrollRootRef = null,
}) => {
    const [historyChats, setHistoryChats] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loaderRef = useRef(null);

    // Load agent chat history with pagination
    const loadAgentChats = useCallback(async (pageNum = 1) => {
        if (!agentSlug) return;

        try {
            setHistoryError(null);
            if (pageNum === 1) {
                setIsLoadingHistory(true);
            }

            let result;
            if (chatType === 'my-chats') {
                // Load only current user's chats with this agent
                result = await getUserAgentChats(agentSlug, pageNum, 50, accessToken);
            } else {
                // Load all chats for this agent (owner view)
                result = await getAgentChats(agentSlug, pageNum, 50, accessToken);
            }

            setHistoryChats(prev => pageNum === 1 ? result.chats : [...prev, ...result.chats]);
            setHasMore(result.hasMore);
            setPage(pageNum);
        } catch (error) {
            console.error('Error loading agent chats:', error);
            setHistoryError(error.message);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [agentSlug, chatType, accessToken]);

    // Handle load more chats
    const handleLoadMore = useCallback(() => {
        return loadAgentChats(page + 1);
    }, [loadAgentChats, page]);

    // Intersection observer for auto-loading
    const handleIntersection = useCallback(entries => {
        const target = entries[0];

        // Check internet connection
        if (!navigator.onLine) {
            return;
        }

        if (target.isIntersecting && hasMore && !isLoadingMore) {
            setIsLoadingMore(true);
            handleLoadMore().finally(() => setIsLoadingMore(false));
        }
    }, [hasMore, isLoadingMore, handleLoadMore]);

    // Set up intersection observer
    useEffect(() => {
        const options = {
            root: scrollRootRef?.current ?? null,
            rootMargin: '200px',
            threshold: 0,
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => observer.disconnect();
    }, [handleIntersection, scrollRootRef]);

    // Initial load
    useEffect(() => {
        loadAgentChats();
    }, [loadAgentChats]);

    // Handle chat click - open in new window
    const handleChatClick = useCallback(chat => {
        const chatUrl = `/a/${chat.id}`;
        window.open(chatUrl, '_blank', 'noopener,noreferrer');
    }, []);

    // Format date function
    const { current: formatDate } = useRef(date => {
        if (!date) return 'N/A';

        const dateObj = typeof date === 'string' ? new Date(date) : date;

        // Check if date is valid
        if (isNaN(dateObj.getTime())) return 'Invalid Date';

        return dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    });

    // Get participants display text
    const { current: getParticipantsText } = useRef(participants => {
        const userParticipants = participants.filter(p => p.type === 'USER');
        const knownUsers = userParticipants.filter(p => p.user);

        if (userParticipants.length === 0) return 'Guest';
        if (knownUsers.length === 0) return 'Guest';
        if (knownUsers.length === 1) return `${knownUsers[0].user.firstName || '1 participant'}`;
        return `${knownUsers.length} participants`;
    });

    // Render history chat item (no status)
    const renderHistoryChatItem = useCallback((chat, index) => (
        <motion.div
            key={chat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.4,
                delay: index * 0.05,
                ease: "easeOut",
            }}
            className={styles.chatItem}
            onClick={() => handleChatClick(chat)}
        >
            <div className={styles.chatContent}>
                <div className={styles.chatHeader}>
                    <span className={styles.chatName}>{chat.title}</span>
                </div>
                <div className={styles.chatMeta}>
                    <span className={styles.metaItem}>
                        Created: {formatDate(chat.createdAt)}
                    </span>
                    <span className={styles.metaItem}>
                        Last activity: {formatDate(chat.lastActivity)}
                    </span>
                    <span className={styles.metaItem}>
                        {chat.messagesCount} messages
                    </span>
                    {chat.participants && (
                        <span className={styles.metaItem}>
                            {getParticipantsText(chat.participants)}
                        </span>
                    )}
                </div>
            </div>
        </motion.div>
    ), [handleChatClick, formatDate, getParticipantsText]);

    // Get section title based on chat type
    const getSectionTitle = () => {
        if (chatType === 'my-chats') {
            return 'My Chat History';
        }
        return 'All Agent Chats';
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05,
                delayChildren: 0.1,
            },
        },
    };

    return (
        <motion.div 
            className={styles.container}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <div className={styles.chatsSection}>
                <motion.h3 
                    className={styles.sectionTitle}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {getSectionTitle()}
                </motion.h3>
                <div className={styles.chatsList}>
                    {isLoadingHistory && !isLoadingMore ? (
                        <motion.div 
                            className={styles.loadingState}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <MoonLoader color="#000000" size={24} />
                        </motion.div>
                    ) : historyError ? (
                        <motion.div 
                            className={styles.errorState}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <p>Error: {historyError}</p>
                        </motion.div>
                    ) : historyChats.length > 0 ? (
                        <>
                            {historyChats.map((chat, index) => renderHistoryChatItem(chat, index))}
                            {hasMore && (
                                <div ref={loaderRef} className={styles.bottomLoader}>
                                    {isLoadingMore && (
                                        <MoonLoader color="#000000" size={20} />
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <motion.div
                            className={styles.emptyState}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <p>No chats found</p>
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default memo(AgentChatsList);
